import { useEffect, useState, useCallback } from 'react';
import {
  Users,
  UserPlus,
  Trash2,
  CheckCircle,
  AlertCircle,
  X,
  Mail,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { User } from '../types';
import api from '../services/api';

const roleBadgeClasses: Record<string, string> = {
  admin: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  super_user: 'bg-purple-100 text-purple-800 border-purple-200',
  analyst: 'bg-slate-100 text-slate-800 border-slate-200',
};

export default function UsersPage() {
  const currentUser = useAuthStore((state) => state.user);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successBanner, setSuccessBanner] = useState<string | null>(null);

  // Modal State
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [newEmail, setNewEmail] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [newRole, setNewRole] = useState<User['role']>('analyst');
  const [submitting, setSubmitting] = useState<boolean>(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.users.list();
      setUsers(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch team users';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail || !newPassword) return;

    try {
      setSubmitting(true);
      setError(null);
      const created = await api.users.create({
        email: newEmail,
        password: newPassword,
        role: newRole,
      });

      setUsers((prev) => [created, ...prev]);
      setShowAddModal(false);
      setNewEmail('');
      setNewPassword('');
      setNewRole('analyst');
      setSuccessBanner(`Team member ${created.email} added successfully.`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create user.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleChange = async (userId: string, targetRole: User['role']) => {
    try {
      setError(null);
      const updated = await api.users.updateRole(userId, targetRole);
      setUsers((prev) => prev.map((u) => (u.id === userId ? updated : u)));
      setSuccessBanner(`Role updated for ${updated.email} to ${targetRole}.`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update user role.';
      setError(msg);
    }
  };

  const handleDeleteUser = async (userId: string, email: string) => {
    if (!window.confirm(`Are you sure you want to deactivate ${email}?`)) return;

    try {
      setError(null);
      await api.users.delete(userId);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      setSuccessBanner(`User ${email} removed successfully.`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to delete user.';
      setError(msg);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans text-slate-900 pb-16">
      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-6">
        {/* Banner Messages */}
        {successBanner && (
          <div className="rounded-lg bg-emerald-50 p-4 border border-emerald-200 text-xs text-emerald-950 flex items-center justify-between shadow-xs">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
              <p className="font-medium">{successBanner}</p>
            </div>
            <button
              onClick={() => setSuccessBanner(null)}
              className="text-emerald-700 hover:text-emerald-900 p-1 cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {error && (
          <div className="rounded-lg bg-rose-50 p-4 border border-rose-200 text-xs text-rose-950 flex items-center justify-between shadow-xs">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              <p className="font-medium">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-rose-700 hover:text-rose-900 p-1 cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Page Header */}
        <div className="bg-white rounded-xl p-6 md:p-8 border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.03)] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-blue-800 bg-blue-50 border border-blue-200 px-2.5 py-0.5 rounded">
                <Users className="w-3 h-3 text-blue-600" />
                Access Control
              </span>
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
              Team Members & Role Governance
            </h1>
            <p className="text-xs text-slate-600 max-w-2xl leading-relaxed">
              Govern platform user accounts, assign authorization levels (Analyst, Admin, Super
              User), and enforce 21 CFR Part 11 electronic signature & access policies.
            </p>
          </div>

          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-700 hover:bg-blue-800 active:scale-[0.98] text-white text-xs font-semibold px-4 py-2.5 rounded-lg transition flex items-center gap-2 shadow-xs cursor-pointer shrink-0"
          >
            <UserPlus className="w-4 h-4" />
            <span>Add Team Member</span>
          </button>
        </div>

        {/* User List Table */}
        <div className="bg-white rounded-xl border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.03)] overflow-hidden">
          {loading ? (
            <div className="p-16 text-center">
              <div className="inline-block w-8 h-8 border-3 border-blue-700 border-t-transparent rounded-full animate-spin mb-3"></div>
              <p className="text-slate-600 text-xs font-semibold">Loading team accounts...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-16 text-center">
              <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <h3 className="font-display text-base font-semibold text-slate-900">
                No users found
              </h3>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                    <th className="py-3 px-4">User Email</th>
                    <th className="py-3 px-4">Assigned Role</th>
                    <th className="py-3 px-4">Role Selector</th>
                    <th className="py-3 px-4">Created Date</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-3.5 px-4 font-semibold text-slate-900 flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 text-slate-400" />
                        <span>{u.email}</span>
                        {currentUser?.id === u.id && (
                          <span className="bg-blue-50 text-blue-900 text-[10px] px-2 py-0.5 rounded font-bold border border-blue-200">
                            You
                          </span>
                        )}
                      </td>
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <span
                          className={`px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${
                            roleBadgeClasses[u.role] || 'bg-slate-100 text-slate-800'
                          }`}
                        >
                          {u.role.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <select
                          value={u.role}
                          disabled={currentUser?.id === u.id}
                          onChange={(e) => handleRoleChange(u.id, e.target.value as User['role'])}
                          className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                        >
                          <option value="analyst">Analyst</option>
                          <option value="admin">Admin</option>
                          {(currentUser?.role === 'super_user' || u.role === 'super_user') && (
                            <option value="super_user">Super User</option>
                          )}
                        </select>
                      </td>
                      <td className="py-3.5 px-4 whitespace-nowrap text-slate-500 font-mono text-[11px] tabular-nums">
                        {new Date(u.created_at || Date.now()).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </td>
                      <td className="py-3.5 px-4 text-right whitespace-nowrap">
                        <button
                          onClick={() => handleDeleteUser(u.id, u.email)}
                          disabled={currentUser?.id === u.id}
                          className="text-rose-700 hover:text-rose-900 disabled:opacity-30 disabled:cursor-not-allowed p-1 rounded hover:bg-rose-50 transition cursor-pointer"
                          title={
                            currentUser?.id === u.id ? 'Cannot deactivate self' : 'Deactivate user'
                          }
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 border border-slate-200 shadow-xl space-y-4">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-display text-lg font-semibold text-slate-900">Add Team Member</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  User Work Email
                </label>
                <input
                  type="email"
                  required
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="analyst@spacelabs.com"
                  className="w-full text-xs bg-slate-50 border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Initial Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full text-xs bg-slate-50 border border-slate-200 rounded-lg p-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer focus:outline-none"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Authorization Role
                </label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value as User['role'])}
                  className="w-full text-xs bg-slate-50 border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600 font-medium"
                >
                  <option value="analyst">Analyst (Triage & Query)</option>
                  <option value="admin">Admin (Team, Rules, System)</option>
                  {currentUser?.role === 'super_user' && (
                    <option value="super_user">Super User (Full System Access)</option>
                  )}
                </select>
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-xs font-semibold text-white bg-blue-700 hover:bg-blue-800 active:scale-[0.98] disabled:opacity-50 rounded-lg transition shadow-xs cursor-pointer"
                >
                  {submitting ? 'Creating...' : 'Create Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
